import type { QualifyingStatusProduct } from '@iheartjane/data-access';
import { getCartItemWeight, productPhotoMetadata } from '@iheartjane/menu';
import {
  AddIcon,
  Box,
  Flex,
  Image,
  JaneGoldIcon,
  Tag,
  TagFilledIcon,
  TrashIcon,
  Typography,
} from '@iheartjane/reefer';
import { clsx } from 'clsx';

import { useSpecialDetailContext } from './SpecialDetail';
import styles from './SpecialDetail.module.css';

export interface ProductTileProps {
  isDiscounted?: boolean;
  isGold?: boolean;
  isQualifiedForMore?: boolean;
  isUnlocked?: boolean;
  onRemoveProduct?: (product: QualifyingStatusProduct) => void;
  product?: QualifyingStatusProduct;
  tagLabel?: string;
}

export const ProductTile = ({
  isQualifiedForMore,
  isDiscounted,
  isGold,
  isUnlocked,
  onRemoveProduct,
  product,
  tagLabel,
}: ProductTileProps) => {
  const { scrolledFromTop } = useSpecialDetailContext();
  const showTag = isDiscounted && tagLabel;
  const tileSize = scrolledFromTop ? '96px' : '120px';

  const { url: photoUrl } = productPhotoMetadata(product);

  const showWeight = !!product && product?.price_id !== 'each';
  const weightString = showWeight ? ` (${getCartItemWeight(product)})` : '';

  if (!product) {
    return (
      <Flex
        flexDirection="column"
        alignItems="center"
        pt={32}
        width={tileSize}
        className={clsx(styles['specialDetail__transition'], {
          [styles['productTile__more']]: isQualifiedForMore,
        })}
        data-testid={isQualifiedForMore ? 'qualified-for-more' : 'qualifier'}
      >
        <Flex
          background={
            isQualifiedForMore
              ? isGold
                ? 'gold-light'
                : 'sunset-light'
              : 'grays-ultralight'
          }
          borderRadius="lg"
          alignItems="center"
          justifyContent="center"
          height={tileSize}
          width={tileSize}
          className={clsx(styles['productTile__border'], {
            [styles['productTile__borderDashed']]:
              !isUnlocked || isQualifiedForMore,
          })}
        >
          {isQualifiedForMore ? (
            isGold ? (
              <JaneGoldIcon />
            ) : (
              <TagFilledIcon color="sunset" />
            )
          ) : (
            <AddIcon color="transparent-black-twenty" />
          )}
        </Flex>
      </Flex>
    );
  }
  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      gap={8}
      pt={!isDiscounted && !showTag ? 32 : 0}
      width={tileSize}
      className={clsx(
        styles['productTile__fadeIn'],
        styles['specialDetail__transition']
      )}
    >
      {showTag && (
        <Tag
          label={tagLabel}
          icon={
            isGold ? (
              <JaneGoldIcon
                color={isUnlocked ? 'gold' : 'grays-light'}
                className={
                  !isUnlocked ? styles['productTile__goldIcon'] : undefined
                }
              />
            ) : undefined
          }
          color={
            !isUnlocked ? 'grays-mid' : isGold ? 'grays-black' : 'grays-white'
          }
          background={!isUnlocked ? 'grays-light' : isGold ? 'gold' : 'sunset'}
          data-testid="product-tag"
          className={clsx(styles['white__space__no__wrap'], {
            [styles['productTile__goldIcon']]: !isUnlocked,
          })}
        />
      )}

      <Box position="relative">
        <Image
          src={photoUrl}
          height={tileSize}
          width={tileSize}
          altText={product.name}
          borderRadius="24px"
          className={clsx(styles['productTile__border'], {
            [styles['productTile__borderDashed']]:
              !isUnlocked || isQualifiedForMore,
          })}
        />
        <Flex
          top={-6}
          right={-6}
          position="absolute"
          background="grays-white"
          alignItems="center"
          justifyContent="center"
          height="48px"
          width="48px"
          className={styles['specialDetail__iconButton']}
          onClick={() => onRemoveProduct?.(product)}
          aria-label="Remove product"
        >
          <TrashIcon />
        </Flex>
      </Box>

      <Typography
        textAlign="center"
        maxLines={2}
        variant={scrolledFromTop ? 'mini' : 'body'}
      >
        {product.name}
        {weightString}
      </Typography>
    </Flex>
  );
};
